import tryCreateAgendaMailInfoBarMessage from './createAgendaMailInfoBarMessage';
import tryCreateCLPLabelInfoBarMessage from './createCLPLabelInfoBarMessage';
import createDelegateInfoBarMessage from './createDelegateInfoBarMessage';
import createDoNotForwardMeetingMessageInfoBarMessage from './createDoNotForwardMeetingMessageInfoBarMessage';
import createExternalMessageInfobar from './createExternalMessageInfobar';
import createInfoBarMessageViewState from './createInfoBarMessageViewState';
import tryCreateIRMInfoBarMessage from './createIRMInfoBarMessage';
import createItemPartLoadFailureInfoBarMessage from './createItemPartLoadFailureInfoBarMessage';
import createLastUserActionInfoBarMessage from './createLastUserActionInfoBarMessage';
import trycreateMessageApprovalAndVotingInfoBarMessage from './createMessageApprovalAndVotingInfoBarMessage';
import createMessageClassificationInfoBarMessage from './createMessageClassificationInfoBarMessage';
import createMessageSubmittedInfoBarMessage from './createMessageSubmittedInfoBarMessage';
import createNudgeFeedbackInfoBarViewState from './createNudgeFeedbackInfoBarViewState';
import createNudgeInfoBarViewState from './createNudgeInfoBarViewState';
import tryCreatePolicyTagInfoBarMessage from './createPolicyTagInfoBarMessage';
import tryCreateReadReceiptInfoBarMessage from './createReadReceiptInfoBarMessage';
import tryCreateResendInfoBarMessage from './createResendInfoBarMessage';
import tryCreateRevocationInfoBarMessage from './createRevocationInfoBarMessage';
import tryCreateSafetyBarMessageViewState from './createSafetyBarMessageViewState';
import tryCreateSMIMEInfoBarMessage from './createSMIMEInfoBarMessage';
import createSpotlightInfoBarViewState from './createSpotlightInfoBarViewState';
import createSpotlightFeedbackInfoBarViewState from './createSpotlightFeedbackInfoBarViewState';
import tryCreateTranslateInfoBarMessage from './createTranslateInfoBarMessage';
import createUnauthenticatedSenderMessageViewState from './createUnauthenticatedSenderInfoBarMessage';
import tryCreateUnsubscribeInfoBarMessage from './createUnsubscribeInfoBarMessage';
import { logUsage } from 'owa-analytics';
import { formatUserDate, userDate } from 'owa-datetime';
import getInfoBarMessageCreator from 'owa-info-bar/lib/utils/infoBarMessageCreatorsMap';
import loc, { format } from 'owa-localize';
import ItemPartViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemPartViewState';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import { getSensitivityInfoBarMessage } from 'owa-mail-sensitivity';
import { ClientMessage } from 'owa-mail-store';
import { InstrumentationContext } from 'owa-search/lib/types/InstrumentationContext';
import ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import getSmimeDecodeErrorInfoBarMessage from 'owa-smime/lib/utils/getSmimeDecodeErrorInfoBarMessage';
import getSmimeSignatureInfoBarMessage from 'owa-smime/lib/utils/getSmimeSignatureInfoBarMessage';
import {
    AttachmentPolicyInfoBarId,
    getInfoBarViewState,
} from 'owa-attachment-policy-access-issue-checker';
import {
    infoHighImportance,
    infoLowImportance,
    infoFlagForFollowUp,
    infoFlagStartBy,
    infoFlagDueBy,
    infoFlagCompletedOn,
    infoReadReceiptSent,
    infoResendErrorMessage,
} from './getInfoBarMessageViewState.locstring.json';
import {
    InfoBarCustomAction,
    InfoBarOptionsAction,
    InfoBarShowDetailsAction,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

function getImportanceInfoBarMessage(message: ClientMessage): string[] {
    switch (message.Importance) {
        case 'High':
            return [loc(infoHighImportance)];
        case 'Low':
            return [loc(infoLowImportance)];
        default:
            // Show nothing in the default case which handles 'Normal' and undefined
            return [];
    }
}

function getFlagCompleteString(message: ClientMessage): string[] {
    if (!message.Flag || message.Flag.FlagStatus == 'NotFlagged') {
        return [];
    }

    let infoMessage = '';
    const flag = message.Flag;

    if (flag.FlagStatus == 'Flagged') {
        infoMessage = loc(infoFlagForFollowUp);
        infoMessage += flagCompleteStringHelper(loc(infoFlagStartBy), flag.StartDate);
        infoMessage += flagCompleteStringHelper(loc(infoFlagDueBy), flag.DueDate);
    } else if (flag.FlagStatus == 'Complete') {
        infoMessage = loc(infoFlagForFollowUp);
        infoMessage += flagCompleteStringHelper(loc(infoFlagCompletedOn), flag.CompleteDate);
    }
    return [infoMessage];
}

function flagCompleteStringHelper(text: string, date: string) {
    return date ? ' ' + format(text, formatUserDate(userDate(date))) : '';
}

export default function getInfoBarMessageViewState(
    messageId: string,
    message: ClientMessage,
    instrumentationContexts: InstrumentationContext[],
    itemViewState: ItemViewState,
    isItemAttachment: boolean
): InfoBarMessageViewState {
    if (!message) {
        return null;
    }

    let infoMessages: (
        | string
        | InfoBarCustomAction
        | InfoBarShowDetailsAction
        | InfoBarOptionsAction
    )[];
    const infoBarMessageCreator = getInfoBarMessageCreator(itemViewState, messageId);
    if (infoBarMessageCreator) {
        if (typeof infoBarMessageCreator === 'function') {
            return infoBarMessageCreator(itemViewState);
        } else {
            infoMessages = infoBarMessageCreator;
        }
    } else {
        switch (messageId) {
            case 'unsubscribeInfoBar':
                return tryCreateUnsubscribeInfoBarMessage(message, messageId, itemViewState);
            case 'safetyBar':
                return tryCreateSafetyBarMessageViewState(
                    message,
                    messageId,
                    instrumentationContexts,
                    itemViewState,
                    isItemAttachment
                );
            case 'infoImportance':
                infoMessages = getImportanceInfoBarMessage(message);
                break;
            case 'infoSensitivity':
                infoMessages = getSensitivityInfoBarMessage(message.Sensitivity);
                break;
            case 'infoFlagStatus':
                infoMessages = getFlagCompleteString(message);
                break;
            case 'irmInfoBar':
                return tryCreateIRMInfoBarMessage(message, messageId, itemViewState);
            case 'infoMessageSubmitted':
                infoMessages = createMessageSubmittedInfoBarMessage(message);
                break;
            case 'agendaMailFeedback':
                return tryCreateAgendaMailInfoBarMessage(message, messageId, itemViewState);
            case 'unauthenticatedSender':
                return createUnauthenticatedSenderMessageViewState(message, messageId);
            case 'infoDelegate':
                infoMessages = createDelegateInfoBarMessage(message);
                break;
            case 'infoMeetingAttendeeDoNotForward':
                infoMessages = createDoNotForwardMeetingMessageInfoBarMessage(message);
                break;
            case 'infoSMIME':
                return tryCreateSMIMEInfoBarMessage(
                    message,
                    messageId,
                    itemViewState.isConversationItemPart
                );
            case 'infoSmimeDecodeError':
                return getSmimeDecodeErrorInfoBarMessage(
                    message,
                    messageId,
                    itemViewState.isConversationItemPart
                );
            case 'infoSmimeSigned':
                return getSmimeSignatureInfoBarMessage(
                    message,
                    messageId,
                    itemViewState.isConversationItemPart
                );
            case AttachmentPolicyInfoBarId.ReadOnlyAccess:
            case AttachmentPolicyInfoBarId.ReadOnlyPlusAttachmentsBlockedAccess:
                return getInfoBarViewState(messageId);
            case 'infoItemPartLoadFailure':
                return createItemPartLoadFailureInfoBarMessage(message, messageId, itemViewState);
            case 'infoLastUserAction':
                infoMessages = createLastUserActionInfoBarMessage(message, itemViewState);
                break;
            case 'infoReadReceiptRequested':
                return tryCreateReadReceiptInfoBarMessage(message, messageId, itemViewState);
            case 'infoReadReceiptSent':
                infoMessages = [loc(infoReadReceiptSent)];
                break;
            case 'translateInfoBar':
                return tryCreateTranslateInfoBarMessage(message, messageId, itemViewState);
            case 'infoResend':
                return tryCreateResendInfoBarMessage(message, messageId, itemViewState);
            case 'infoResendError':
                infoMessages = [loc(infoResendErrorMessage)];
                break;
            case 'infoPolicyTag':
                infoMessages = tryCreatePolicyTagInfoBarMessage(message);
                break;
            case 'infoMessageApprovalAndVoting':
                infoMessages = trycreateMessageApprovalAndVotingInfoBarMessage(message);
                break;
            case 'nudged':
                return createNudgeInfoBarViewState(message, messageId, itemViewState);
            case 'nudgeFeedback':
                return createNudgeFeedbackInfoBarViewState(message, messageId, itemViewState);
            case 'infoMessageClassification':
                return createMessageClassificationInfoBarMessage(message, messageId);
            case 'infoCLPLabel':
                return tryCreateCLPLabelInfoBarMessage(
                    <ClientItem>message,
                    messageId,
                    <ItemPartViewState>itemViewState
                );
            case 'infoRevocation':
                return tryCreateRevocationInfoBarMessage(
                    <ClientItem>message,
                    messageId,
                    <ItemPartViewState>itemViewState
                );
            case 'externalMessage':
                return createExternalMessageInfobar(
                    message,
                    messageId,
                    itemViewState,
                    instrumentationContexts
                );
            case 'spotlight':
                return createSpotlightInfoBarViewState(message, messageId);
            case 'spotlightFeedback':
                return createSpotlightFeedbackInfoBarViewState(
                    message,
                    messageId,
                    <ItemPartViewState>itemViewState
                );
        }
    }

    if (infoMessages && infoMessages.length > 0) {
        return createInfoBarMessageViewState(messageId, infoMessages);
    } else {
        if (!infoMessages) {
            // If the infoMessage is empty, it means that the message id doesn't have any creator.
            // We log this datapoint to figure out which message id has this issue.
            logUsage('RPInfoBarEmptyCase', [messageId]);
        }
        return null;
    }
}
